import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import {
  getWebinar,
  registerWebinar,
  unregisterWebinar
} from '../../services/api';
import {
  Calendar,
  Clock,
  Users,
  Video,
  CalendarPlus,
  ArrowLeft,
  User,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import './WebinarDetail.css';


const WebinarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [countdown, setCountdown] = useState(null);

  const fetchWebinar = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getWebinar(id);
      setWebinar(response.data.data.webinar);
    } catch (error) {
      console.error('Error fetching webinar:', error);
      showNotification('Error loading webinar', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWebinar();
  }, [fetchWebinar]);

  // Countdown timer
  useEffect(() => {
    if (!webinar || !webinar.is_upcoming) return;

    const interval = setInterval(() => {
      const now = new Date();
      const scheduled = new Date(webinar.scheduled_at);
      const diff = scheduled - now;

      if (diff <= 0) {
        setCountdown(null);
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(interval);
  }, [webinar]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleRegister = async () => {
    if (!user) {
      showNotification('Please login to register for webinars', 'error');
      navigate('/login');
      return;
    }

    try {
      setRegistering(true);
      await registerWebinar(id);
      showNotification('Successfully registered for webinar!');
      fetchWebinar();
    } catch (error) {
      console.error('Error registering:', error);
      showNotification(
        error.response?.data?.message || 'Error registering for webinar',
        'error'
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleUnregister = async () => {
    if (!user) return;

    try {
      setRegistering(true);
      await unregisterWebinar(id);
      showNotification('Successfully unregistered from webinar');
      fetchWebinar();
    } catch (error) {
      console.error('Error unregistering:', error);
      showNotification(
        error.response?.data?.message || 'Error unregistering from webinar',
        'error'
      );
    } finally {
      setRegistering(false);
    }
  };

  const addToCalendar = () => {
    if (!webinar) return;

    const start = new Date(webinar.scheduled_at);
    const end = new Date(start.getTime() + webinar.duration_minutes * 60000);

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icalData = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${formatDate(start)}`,
      `DTEND:${formatDate(end)}`,
      `SUMMARY:${webinar.title}`,
      `DESCRIPTION:${webinar.description || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\n');

    const blob = new Blob([icalData], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'webinar.ics';
    link.click();
    URL.revokeObjectURL(url);

    showNotification('Calendar event downloaded!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!webinar) {
    return (
      <div className="error-container">
        <AlertCircle size={64} />
        <h2>Webinar not found</h2>
        <button className="btn-back" onClick={() => navigate('/webinars')}>
          Back to Webinars
        </button>
      </div>
    );
  }

  return (
    <div className="webinar-detail">
      {/* Notification */}
      {notification.show && (
        <div className={`notification notification-${notification.type}`}>
          <AlertCircle size={20} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/dashboard">Dashboard</Link>
        <span>/</span>
        <Link to="/webinars">Live Streaming</Link>
        <span>/</span>
        <span>{webinar.title}</span>
      </div>

      <div className="webinar-content-wrapper">
        {/* Left Side - Video/Countdown */}
        <div className="webinar-main">
          {/* Live Status Badge */}
          {webinar.is_live && (
            <div className="live-indicator">
              <span className="live-dot"></span>
              LIVE NOW
            </div>
          )}

          {/* Video + Chat Layout */}
          <div className="video-chat-layout">
            {/* Video Section */}
            <div className="video-section">
              <div className="video-container">
                {webinar.is_live && webinar.youtube_video_id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${webinar.youtube_video_id}?autoplay=1`}
                    title={webinar.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : webinar.is_live && !webinar.youtube_video_id ? (
                  <div className="placeholder-container">
                    <Video size={64} strokeWidth={1.5} />
                    <div className="live-indicator" style={{ position: 'relative', display: 'inline-block', marginTop: '20px' }}>
                      <span className="live-dot"></span>
                      LIVE NOW
                    </div>
                    <p>Waiting for live stream to start...</p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                      The instructor will start streaming shortly
                    </p>
                  </div>
                ) : webinar.status === 'ended' && webinar.youtube_video_id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${webinar.youtube_video_id}`}
                    title={webinar.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : webinar.is_upcoming && countdown ? (
                  <div className="countdown-container">
                    <div className="countdown-icon">
                      <Video size={64} strokeWidth={1.5} />
                    </div>
                    <h2>Webinar Starting In</h2>
                    <div className="countdown-timer-large">
                      <div className="countdown-unit">
                        <span className="countdown-value">{countdown.days}</span>
                        <span className="countdown-label">Days</span>
                      </div>
                      <div className="countdown-separator">:</div>
                      <div className="countdown-unit">
                        <span className="countdown-value">{String(countdown.hours).padStart(2, '0')}</span>
                        <span className="countdown-label">Hours</span>
                      </div>
                      <div className="countdown-separator">:</div>
                      <div className="countdown-unit">
                        <span className="countdown-value">{String(countdown.minutes).padStart(2, '0')}</span>
                        <span className="countdown-label">Minutes</span>
                      </div>
                      <div className="countdown-separator">:</div>
                      <div className="countdown-unit">
                        <span className="countdown-value">{String(countdown.seconds).padStart(2, '0')}</span>
                        <span className="countdown-label">Seconds</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-container">
                    <Video size={64} strokeWidth={1.5} />
                    <p>This webinar has ended</p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Section Removed by User Request */}
            {/* {(webinar.is_live || (webinar.status === 'ended' && webinar.youtube_video_id)) && (
              <div className="chat-section">
                 Chat removed
              </div>
            )} */}
          </div>

          {/* Webinar Title & Description */}
          <div className="webinar-info-section">
            <h1>{webinar.title}</h1>
            {webinar.description && (
              <p className="webinar-description">{webinar.description}</p>
            )}
          </div>
        </div>

        {/* Right Side - Info & Actions */}
        <div className="webinar-sidebar">
          {/* Status Card */}
          <div className="info-card">
            <div className={`status-badge-large ${webinar.is_live ? 'live' : webinar.is_upcoming ? 'upcoming' : 'ended'}`}>
              {webinar.is_live ? 'LIVE NOW' : webinar.is_upcoming ? 'UPCOMING' : 'ENDED'}
            </div>

            {/* Schedule Info */}
            <div className="info-item-large">
              <Calendar size={24} />
              <div>
                <p className="info-label">Date</p>
                <p className="info-value">{formatDate(webinar.scheduled_at)}</p>
              </div>
            </div>

            <div className="info-item-large">
              <Clock size={24} />
              <div>
                <p className="info-label">Time</p>
                <p className="info-value">{formatTime(webinar.scheduled_at)}</p>
              </div>
            </div>

            <div className="info-item-large">
              <Clock size={24} />
              <div>
                <p className="info-label">Duration</p>
                <p className="info-value">{webinar.duration_minutes} minutes</p>
              </div>
            </div>

            {/* Instructor Info */}
            {webinar.instructor_name && (
              <div className="info-item-large">
                <User size={24} />
                <div>
                  <p className="info-label">Instructor</p>
                  <p className="info-value">{webinar.instructor_name}</p>
                </div>
              </div>
            )}

            {/* Category */}
            {webinar.category && (
              <div className="category-badge-large">
                {webinar.category}
              </div>
            )}

            {/* Registration Count */}
            <div className="registration-info">
              <Users size={20} />
              <span>{webinar.enrolled_count} students registered</span>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              {webinar.is_upcoming ? (
                <>
                  {webinar.is_registered ? (
                    <>
                      <div className="registered-badge">
                        <CheckCircle size={20} />
                        <span>You're Registered</span>
                      </div>
                      <button
                        className="btn-secondary"
                        onClick={handleUnregister}
                        disabled={registering}
                      >
                        {registering ? 'Unregistering...' : 'Unregister'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={handleRegister}
                      disabled={registering}
                    >
                      {registering ? 'Registering...' : 'Register Now'}
                    </button>
                  )}

                  <button className="btn-calendar" onClick={addToCalendar}>
                    <CalendarPlus size={20} />
                    Add to Calendar
                  </button>
                </>
              ) : webinar.is_live ? (
                <div className="live-message">
                  <span>Join the live session above!</span>
                </div>
              ) : (
                <button className="btn-disabled" disabled>
                  Webinar Ended
                </button>
              )}
            </div>
          </div>

          {/* Back Button */}
          <button className="btn-back-sidebar" onClick={() => navigate('/webinars')}>
            <ArrowLeft size={20} />
            Back to All Webinars
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebinarDetail;